import { create } from 'zustand';
import { User } from '@/types';
import { apiClient } from '@/lib/api';
import { CONFIG } from '@/constants';

interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (username: string) => Promise<User>;
  loginWithUserId: (userId: string) => Promise<void>;
  logout: () => Promise<void>;
  initializeUser: () => Promise<void>;
  clearError: () => void;
}

// Simple localStorage helpers
const USER_STORAGE_KEY = 'fitness_user';
const ALL_USERS_STORAGE_KEY = 'fitness_all_users'; // For gym pass fallback

const saveUserToStorage = (user: User) => {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  
  // Also save to all users list for gym pass lookup
  try {
    const allUsers = JSON.parse(localStorage.getItem(ALL_USERS_STORAGE_KEY) || '[]');
    const existingIndex = allUsers.findIndex((u: User) => u.id === user.id);
    if (existingIndex >= 0) {
      allUsers[existingIndex] = user;
    } else {
      allUsers.push(user);
    }
    localStorage.setItem(ALL_USERS_STORAGE_KEY, JSON.stringify(allUsers));
  } catch (e) {
    console.warn('Failed to save user to all users list:', e);
  }
};

const getUserFromStorage = (): User | null => {
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const getUserByIdFromStorage = (userId: string): User | null => {
  try {
    const allUsers = JSON.parse(localStorage.getItem(ALL_USERS_STORAGE_KEY) || '[]');
    return allUsers.find((u: User) => u.id === userId) || null;
  } catch {
    return null;
  }
};

const clearUserFromStorage = () => {
  localStorage.removeItem(USER_STORAGE_KEY);
  // Don't clear all users - keep for gym pass recovery
};

export const useUserStore = create<UserState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (username: string) => {
    set({ isLoading: true, error: null });

    try {
      const user = await apiClient.authenticate(username);

      // Save user to localStorage for persistence
      saveUserToStorage(user);

      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      return user;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Login failed',
        isLoading: false,
      });
      throw error;
    }
  },

  loginWithUserId: async (userId: string) => {
    set({ isLoading: true, error: null });

    try {
      // Try to get user from server first
      let user: User | null = null;
      
      try {
        user = await apiClient.getUserById(userId);
      } catch (serverError) {
        // If there's a server error, we'll fall back to localStorage
        user = null;
      }

      // If not found on server, check localStorage as fallback
      if (!user) {
        user = getUserByIdFromStorage(userId);
        
        // REMOVED DANGEROUS AUTO-RESTORE
        // We should NEVER automatically write to production without explicit user action
        // If user exists locally but not on server, just use the local version
        if (user) {
          console.log('Using user from localStorage (not on server):', user.username);
          console.warn('User exists locally but not on server. This may indicate data loss.');
        }
      }

      if (!user) {
        const errorMsg = 'Invalid Member ID. This gym pass is not registered.';
        set({
          error: errorMsg,
          isLoading: false,
        });
        throw new Error(errorMsg);
      }

      // Update last seen and save
      user.lastSeen = Date.now();
      saveUserToStorage(user);

      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login with gym pass failed';
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });

    try {
      // Clear local data
      clearUserFromStorage();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Logout failed',
        isLoading: false,
      });
    }
  },

  initializeUser: async () => {
    set({ isLoading: true });

    try {
      const user = getUserFromStorage();

      if (user) {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Failed to initialize user:', error);
      set({
        user: null,
        isAuthenticated: false,
        error: error instanceof Error ? error.message : 'Failed to initialize user',
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));